import type { KnowledgeArticle } from "../types"

export const watIsEenAiAgent: KnowledgeArticle = {
  id: "insight:wat-is-een-ai-agent",
  pageType: "knowledge-article",
  slug: { nl: "wat-is-een-ai-agent" },
  title: {
    nl: "Wat is een AI Agent? | Uitleg, voorbeelden en verschil met een chatbot",
    en: "Wat is een AI Agent? | Uitleg, voorbeelden en verschil met een chatbot",
  },
  description: {
    nl: "Wat is een AI agent? Uitleg in gewone taal: hoe een agent werkt, het verschil met een chatbot, praktijkvoorbeelden en wanneer een agent wel of niet zinvol is.",
    en: "Wat is een AI agent? Uitleg in gewone taal: hoe een agent werkt, het verschil met een chatbot, praktijkvoorbeelden en wanneer een agent wel of niet zinvol is.",
  },
  h1: {
    nl: "Wat is een AI agent?",
    en: "Wat is een AI agent?",
  },
  published: "2026-10-29",
  modified: "2026-10-29",
  author: "Camiel Schmeitz",
  sections: [
    {
      type: "paragraph",
      text: "Een AI agent is software die met behulp van een taalmodel zelfstandig een taak uitvoert: hij begrijpt een opdracht, bedenkt de stappen, gebruikt daarvoor tools zoals databases en API's, en controleert het resultaat. Een chatbot geeft antwoorden; een agent komt in actie.",
    },
    {
      type: "heading",
      text: "Hoe werkt een AI agent?",
    },
    {
      type: "paragraph",
      text: "Onder de motorkap draait een agent in een lus. Het taalmodel leest de opdracht, kiest de eerstvolgende stap, laat een tool die stap uitvoeren en leest het resultaat. Daarna beslist het model of de taak af is of dat er nog een stap nodig is. Die lus herhaalt zich tot het doel bereikt is, of tot een grens (aantal stappen, kosten, een goedkeurmoment) de agent stopt.",
    },
    {
      type: "list",
      items: [
        "Model: het taalmodel dat de opdracht begrijpt en beslist wat de volgende stap is.",
        "Tools: de acties die de agent mag uitvoeren, zoals zoeken in een kennisbank, een systeem raadplegen of een bericht klaarzetten.",
        "Kennis: toegang tot jouw documentatie en data, zodat antwoorden kloppen met jouw organisatie in plaats van met algemene modelkennis.",
        "Grenzen: wat de agent wel en niet mag, wanneer een mens moet goedkeuren, en logging van elke actie.",
      ],
    },
    {
      type: "heading",
      text: "Wat is het verschil tussen een AI agent en een chatbot?",
    },
    {
      type: "paragraph",
      text: "Een klassieke chatbot volgt een vast script of beantwoordt alleen vragen. Een AI agent kan meerdere stappen achter elkaar zetten en daarbij zelf kiezen welke tool hij nodig heeft. Een chatbot die vragen beantwoordt uit je eigen documenten (een RAG-chatbot) zit daar tussenin: slim in antwoorden, maar zonder zelfstandige acties. Een agent gaat een stap verder doordat hij ook iets doet met het antwoord.",
    },
    {
      type: "heading",
      text: "Voorbeelden uit de praktijk",
    },
    {
      type: "list",
      items: [
        "Navigatieassistent: bij Burgerkracht bouwden we een conversationele assistent die begrijpt waar een bezoeker naar op zoek is, in de kennisbank zoekt en doorverwijst naar de juiste groepen en informatie.",
        "Redactionele workflow: bij De Limburger ondersteunt AI een publicatieproces, met menselijke controle op de punten waar het ertoe doet.",
        "Interne kennisassistent: medewerkers stellen een vraag in gewone taal en de agent zoekt de relevante procedures en documenten bij elkaar.",
        "Verwerking van inkomende berichten: een agent leest een bericht, bepaalt wat het is, haalt de benodigde gegevens op en bereidt een antwoord of vervolgactie voor.",
      ],
    },
    {
      type: "heading",
      text: "Wanneer is een AI agent zinvol?",
    },
    {
      type: "paragraph",
      text: "Een agent past bij werk waar interpretatie voor nodig is: de inhoud varieert, de situatie verschilt per geval en een vast stappenplan schiet tekort. Volgt een proces altijd dezelfde stappen, dan is gewone automatisering goedkoper en betrouwbaarder. Heeft de agent geen toegang tot goede kennis of systemen, dan blijft elk antwoord oppervlakkig.",
    },
    {
      type: "heading",
      text: "Waar moet je op letten?",
    },
    {
      type: "list",
      items: [
        "Begin met één afgebakende taak en meet het resultaat voordat je het bereik vergroot.",
        "Laat een mens goedkeuren waar een fout echte gevolgen heeft; vertrouwen groeit per taak.",
        "Houd de kennis van de agent actueel: verouderde documentatie leidt tot stellig verkeerde antwoorden.",
        "Test op realistische situaties uit je eigen praktijk, niet alleen op een demo.",
      ],
    },
    {
      type: "callout",
      title: "Een AI agent laten maken?",
      text: "Lees in 'AI agent laten maken of bouwen' wat er komt kijken bij een agent die veilig met je bedrijfsprocessen werkt, en in 'Wat kost een AI agent?' waar de kosten door bepaald worden. Of neem contact op: in een gratis kennismaking beoordelen we eerlijk of een agent in jouw situatie zinvol is.",
    },
  ],
  relatedServiceIds: ["ai-agents", "ai-knowledge-systems"],
  relatedCaseIds: ["burgerkracht"],
}
